import { useCallback, useEffect, useRef, useState } from "react";

import { styled } from "@linaria/react";
import { PauseIcon, PlayIcon } from "@phosphor-icons/react";

import Button, { ButtonSize, ButtonVariant } from "@gx/dls/buttons/Button";
import Text, { TextSize, TextVariant } from "@gx/dls/text/Text";
import { withTheme } from "@gx/dls/theme";
import type { PropsWithTheme } from "@gx/dls/theme/types";

import { formatTimeMs } from "@/utils/datetimes";

const ControlsWrapper = withTheme(styled.div<PropsWithTheme>`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 40px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 12px;
  z-index: 10;
  border-top: 0.5px solid ${({ theme }) => theme.color.border.primary};
`);

const ProgressWrapper = styled.div`
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
  cursor: pointer;
`;

const ProgressTrack = withTheme(styled.div<PropsWithTheme>`
  position: relative;
  width: 100%;
  height: 3px;
  background: ${({ theme }) => theme.color.opacity.bw24Alt};
  border-radius: 200px;
`);

const ProgressFill = withTheme(styled.div<PropsWithTheme>`
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  background: ${({ theme }) => theme.color.icon.primary};
  border-radius: 200px;
  z-index: 1;
`);

const ProgressHandle = withTheme(styled.div<
  PropsWithTheme<{
    $isDragging: boolean;
  }>
>`
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 8px;
  height: 8px;
  background: ${({ theme }) => theme.color.icon.secondary};
  border-radius: 50%;
  cursor: grab;
  z-index: 3;
  pointer-events: auto;

  &:active {
    cursor: grabbing;
    transform: translate(-50%, -50%) scale(1.1);
  }

  &:hover {
    transform: translate(-50%, -50%) scale(1.1);
  }
`);

interface BrowserbasePlayerControlsProps {
  isPlaying: boolean;
  currentTime: number;
  totalTime: number;
  onPlayPause: () => void;
  onSeek: (time: number) => void;
}

export function BrowserbasePlayerControls({
  isPlaying,
  currentTime,
  totalTime,
  onPlayPause,
  onSeek,
}: BrowserbasePlayerControlsProps) {
  const [isDragging, setIsDragging] = useState(false);
  const progressRef = useRef<HTMLDivElement>(null);

  const percentage =
    totalTime > 0 ? Math.max(0, Math.min(100, (currentTime / totalTime) * 100)) : 0;

  const handleSeekFromEvent = useCallback(
    (clientX: number) => {
      if (!progressRef.current || totalTime <= 0) return;

      const rect = progressRef.current.getBoundingClientRect();
      const x = clientX - rect.left;
      const pct = Math.max(0, Math.min(100, (x / rect.width) * 100));
      const newTime = (pct / 100) * totalTime;
      onSeek(newTime);
    },
    [totalTime, onSeek],
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      setIsDragging(true);
      handleSeekFromEvent(e.clientX);
    },
    [handleSeekFromEvent],
  );

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      handleSeekFromEvent(e.clientX);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, handleSeekFromEvent]);

  const handleLeft = `calc(${percentage}% - ${(percentage / 100) * 8}px)`;

  return (
    <ControlsWrapper>
      <Button
        variant={ButtonVariant.TERTIARY}
        size={ButtonSize.SMALL}
        icon={isPlaying ? PauseIcon : PlayIcon}
        onClick={onPlayPause}
      />
      <ProgressWrapper ref={progressRef} onMouseDown={handleMouseDown}>
        <ProgressTrack>
          <ProgressFill style={{ width: `${percentage}%` }} />
        </ProgressTrack>
        <ProgressHandle $isDragging={isDragging} style={{ left: handleLeft }} />
      </ProgressWrapper>
      <Text size={TextSize.CAPTION} variant={TextVariant.PRIMARY} isMonospace>
        {formatTimeMs(currentTime)} / {formatTimeMs(totalTime)}
      </Text>
    </ControlsWrapper>
  );
}
